import { ChangeEvent, FormEvent, useRef, useState } from "react";

import {
  Button,
  Field,
  Key,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ServiceIcon,
  Text,
  TextInput,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";

import { Icon } from "@zapier/zinnia-icons";
import { SharingTypeahead, useAutoSelectInputText } from "@zapier/shared";

import styles from "./AddConnectionModal.module.css";

import {
  NotificationType,
  PLACEHOLDER_APP_ICON,
  useNotification,
} from "@zapier/utils";
import {
  getMessageFromNetworkError,
  useAppConnectionSharingConfig,
  useAuthenticationSharing,
  useRenameAuthentication,
} from "@zapier/client";
import { useTranslations } from "@zapier/i18n";
import type { CreateSharesOutput } from "@zapier/bff";
import { ConnectionShareType } from "../types";
import { ConnectionShareModalList } from "../ConnectionShareModal/ConnectionShareModalList";
import {
  ConnectionShare,
  usePendingConnectionSharesState,
  getFailedShareName,
} from "../ConnectionShareModal/utils";

const CONNECTION_NAME_MAX_LENGTH = 255;

export interface NewManagedConnection {
  id: number;
  appName: string;
  appImage?: string | null;
}

/** Step 2 of managed-mode: name + share the connection just created in step 1.
 * Rendered inside the SAME ModalDialogRoot as the app picker (no close/reopen
 * between steps) so the whole flow reads as one dialog, not two. */
export function NewManagedConnectionStep({
  connection,
  onCancel,
  onComplete,
}: {
  connection: NewManagedConnection;
  onCancel: () => void;
  onComplete: (isShared: boolean) => void;
}) {
  const t = useTranslations("new-managed-connection-modal");
  const nameInputIdentifier = "new-managed-connection-name";

  const [name, setName] = useState(connection.appName);
  const isValid = name.length > 0 && name.length <= CONNECTION_NAME_MAX_LENGTH;

  // Guards against double-submit (fast double-click, or Enter repeated while
  // the mutation sequence below is still in flight). A ref is used alongside
  // the state so the check is synchronous — state updates alone wouldn't
  // block a second submit event that fires before React re-renders.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const { createNotification } = useNotification();
  const { rename } = useRenameAuthentication();
  const { createAuthShares } = useAuthenticationSharing();
  const { shareWithEveryoneEnabled, isLoading: isSharingConfigLoading } =
    useAppConnectionSharingConfig();
  // A freshly created connection never has existing shares.
  const { onAdd, onRemove, toAdd, shares, isSharesLoading } =
    usePendingConnectionSharesState([]);

  useAutoSelectInputText({
    isOpen: true,
    inputSelector: `#${nameInputIdentifier}`,
    value: connection.appName,
  });

  const removeShare = ({
    id,
    shareType,
  }: {
    id: Key;
    shareType: ConnectionShareType;
  }) => {
    onRemove(id, shareType);
  };

  const selectShare = (selectedId: Key | null) => {
    if (!selectedId || selectedId === "null") return;
    if (String(selectedId).includes("customuser")) {
      onAdd(
        String(selectedId).replace(/^customuser_/, ""),
        ConnectionShareType.Member,
      );
    } else if (String(selectedId).includes("account")) {
      onAdd("all", ConnectionShareType.All);
    } else {
      onAdd(
        String(selectedId).replace(/^(group_|team_)/, ""),
        ConnectionShareType.Group,
      );
    }
  };

  // The form's native submit is the single trigger for this: the Save button
  // is type="submit" (so Enter-to-submit works for free) and preventDefault
  // stops the browser from following through with an actual page navigation.
  //
  // Rename and share are awaited in sequence rather than fired-and-forgotten:
  // showing success and closing the modal before either mutation resolves
  // would misreport a connection as fully renamed/shared when it might not
  // be. On failure, the modal stays open so the user can see the error and
  // retry — it only closes once everything has actually succeeded.
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isValid || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      try {
        await new Promise((resolve, reject) => {
          rename(
            { name, id: connection.id },
            { onSuccess: resolve, onError: reject },
          );
        });
      } catch (error) {
        createNotification({
          title: t("notifications.unable-to-rename"),
          message: getMessageFromNetworkError(error as any),
          type: "error",
        });
        return;
      }

      const membersToAdd: ConnectionShare[] = toAdd.map((share) => ({
        ...share,
        shareType: share.shareType as ConnectionShareType,
      }));

      // Reflects the server-confirmed successful count, not just whether sharing was attempted.
      let isShared = false;
      let completionNotification: { message: string; type: NotificationType } =
        {
          message: t("notifications.connection-updated", { name }),
          type: "success",
        };

      if (membersToAdd.length > 0) {
        let shareResult: CreateSharesOutput;
        try {
          shareResult = await new Promise((resolve, reject) => {
            createAuthShares(
              {
                connectionId: connection.id,
                shares: membersToAdd.map(({ id, shareType }) => ({
                  id: String(id),
                  shareType,
                })),
              },
              { onSuccess: resolve, onError: reject },
            );
          });
        } catch (error) {
          createNotification({
            title: t("notifications.unable-to-share"),
            message: getMessageFromNetworkError(error as any),
            type: "error",
          });
          return;
        }

        isShared = shareResult.successful > 0;

        if (shareResult.failed > 0) {
          if (!isShared) {
            createNotification({
              title: t("notifications.unable-to-share"),
              message: t("notifications.unable-to-share-detail"),
              type: "error",
            });
            return;
          }
          const failedNames = shareResult.failedShares
            .map((share) => getFailedShareName(share, membersToAdd))
            .join(", ");
          completionNotification = {
            message: t("notifications.partially-shared", {
              name,
              failedNames,
            }),
            type: "warning",
          };
        }
      }

      createNotification(completionNotification);
      onComplete(isShared);
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ModalDialogHeader textAlign="start">
        {t("header.title")}
      </ModalDialogHeader>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events -- the form deliberately stops click propagation to keep the modal open; this isn't a clickable action, so no key handler applies */}
      <form
        onSubmit={submit}
        onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
      >
        <ModalDialogBody textAlign="start">
          <div className={styles.connectionSummary}>
            <ServiceIcon
              serviceName={connection.appName}
              size="x-small"
              src={connection.appImage || PLACEHOLDER_APP_ICON}
            />
            <Text type="Body3" tag="span">
              {connection.appName}
            </Text>
            <Tooltip
              content={t("labels.managed-connection")}
              placement="bottom"
            >
              <TooltipFocusableTrigger
                aria-label={t("labels.managed-connection")}
              >
                <Icon
                  aria-hidden={true}
                  name="miscSecurity"
                  size={16}
                  isBlock={true}
                  color="GrayWarm6"
                />
              </TooltipFocusableTrigger>
            </Tooltip>
            <Icon
              aria-hidden={true}
              name="formCheckCircle"
              size={16}
              color="PrimeGreen"
            />
            <Text type="SmallPrint1" color="TextWeaker" tag="span">
              {t("labels.connected")}
            </Text>
          </div>
          <Field
            label={t("labels.name")}
            inputId={nameInputIdentifier}
            isRequired={true}
            renderInput={(inputProps) => (
              <TextInput
                {...inputProps}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the input when this step renders
                autoFocus={true}
                id={nameInputIdentifier}
                isErrored={!isValid}
                defaultValue={connection.appName}
                isFullWidth={true}
                maxLength={CONNECTION_NAME_MAX_LENGTH}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  setName(event.target?.value || "")
                }
              />
            )}
          />
          <div className={styles.shareSection}>
            <SharingTypeahead
              isBusy={isSharingConfigLoading}
              isDisabled={isSharingConfigLoading}
              disableEveryoneOption={!shareWithEveryoneEnabled}
              onSelect={selectShare}
              showEveryoneOption={true}
              size="medium"
            />
            <div className={styles.shareList}>
              <ConnectionShareModalList
                disableShare={false}
                isLoading={isSharesLoading}
                onRemove={removeShare}
                shares={shares}
              />
            </div>
          </div>
        </ModalDialogBody>
        <ModalDialogFooter>
          <Button variant="secondary" onPress={onCancel} type="button">
            {t("buttons.cancel")}
          </Button>
          <Button
            isDisabled={!isValid || isSubmitting}
            isPending={isSubmitting}
            type="submit"
          >
            {t("buttons.save")}
          </Button>
        </ModalDialogFooter>
      </form>
    </>
  );
}
